import React from 'react';
import { X, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getPlayableVideoUrl } from '../utils/proxyMedia';

/**
 * Reusable 4-column video preview modal
 * Columns: Video, Most Distinct/Text Overlay, Visual, Audio
 * Optional onCreateBrief(video): when provided, shows a prominent "Create brief" button.
 */
const VideoPreviewModal = ({ videoPreview, onClose, formatNumber, highlightTimestamps, onCreateBrief }) => {
  const { isDark } = useTheme();

  if (!videoPreview) return null;

  const video = videoPreview.video || {};
  const canCreateBrief = typeof onCreateBrief === 'function' && video.id;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className={`relative w-full max-w-7xl ${isDark ? 'bg-zinc-900' : 'bg-white'} rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 ${isDark ? 'bg-zinc-800 border-b border-zinc-700' : 'bg-gray-50 border-b border-gray-200'}`}>
          <div className="flex items-center gap-3">
            {videoPreview.platform === 'tiktok' ? (
              <div className="w-6 h-6 bg-black rounded flex items-center justify-center text-[10px] text-white">TT</div>
            ) : (
              <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[10px] text-white">IG</div>
            )}
            <div>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>@{videoPreview.creator}</div>
              <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                {videoPreview.views > 0 && (
                  <span>{formatNumber(videoPreview.views)} views</span>
                )}
                {videoPreview.likes > 0 && (
                  <span>{formatNumber(videoPreview.likes)} likes</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCreateBrief && (
              <button
                onClick={() => onCreateBrief(video)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isDark
                    ? 'bg-cyan-500 hover:bg-cyan-400 text-white shadow-[0_0_14px_rgba(6,182,212,0.35)]'
                    : 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-[0_0_14px_rgba(6,182,212,0.3)]'
                }`}
                title="Generate brief from this video"
              >
                <Sparkles className="h-4 w-4" />
                Generate brief
              </button>
            )}
            <button
              onClick={onClose}
              className={`w-8 h-8 ${isDark ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-200 hover:bg-gray-300'} rounded-full flex items-center justify-center transition-colors`}
              aria-label="Close"
            >
              <X className={`h-5 w-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
            </button>
          </div>
        </div>

        {/* Main Content - 4 Columns: Video, Audio, Visual, Most Distinct/Text Overlay */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Column 1: Video Preview - 9:16 Aspect Ratio */}
            <div className="bg-black rounded-lg overflow-hidden w-full">
              <div className="w-full aspect-[9/16] flex items-center justify-center">
                {videoPreview.videoUrl ? (
                  <video
                    src={getPlayableVideoUrl(videoPreview.videoUrl)}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay
                    playsInline
                    muted={false}
                    preload="metadata"
                    onError={(e) => {
                      console.error('Video failed to load:', videoPreview.videoUrl);
                    }}
                  />
                ) : (
                  <div className={`flex flex-col items-center justify-center gap-3 p-6 text-center ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                    <p className="text-sm">Video can’t be played inline.</p>
                    {(video?.source_url || videoPreview.videoUrl) && (
                      <a
                        href={video?.source_url || videoPreview.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-4 py-2 rounded-lg font-medium ${isDark ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-cyan-500 hover:bg-cyan-600 text-white'}`}
                      >
                        Watch on {videoPreview.platform === 'instagram' ? 'Instagram' : 'TikTok'} →
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Audio - 3rd metadata column (closest to video) */}
            <div className={`${isDark ? 'bg-purple-900/30 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg border flex flex-col w-full aspect-[9/16]`}>
              <div className="flex items-center gap-2 mb-3 p-4 pb-2 flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Audio</h3>
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {video.audio_description ? (
                  <div className={`text-sm ${isDark ? 'text-purple-100' : 'text-gray-700'} leading-relaxed space-y-2`}>
                    {video.audio_description.split(/[.!?]+/).filter(s => s.trim().length > 0).map((sentence, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                        <span>{highlightTimestamps ? highlightTimestamps(sentence.trim()) : sentence.trim()}{idx < video.audio_description.split(/[.!?]+/).filter(s => s.trim().length > 0).length - 1 && '.'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'} italic`}>No audio description available</p>
                )}
                {/* Music Info */}
                {video.apify_music_name && (
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-purple-700' : 'border-purple-200'}`}>
                    <div className={`text-xs font-semibold ${isDark ? 'text-purple-300' : 'text-gray-600'} uppercase mb-1`}>Music</div>
                    <div className={`text-sm ${isDark ? 'text-purple-100' : 'text-gray-700'}`}>
                      {video.apify_music_name}
                      {video.apify_music_author && (
                        <span className={isDark ? 'text-purple-300' : 'text-gray-500'}> by {video.apify_music_author}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: Visual - 2nd metadata column */}
            <div className={`${isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-lg border flex flex-col w-full aspect-[9/16]`}>
              <div className="flex items-center gap-2 mb-3 p-4 pb-2 flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Visual</h3>
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {video.visual_description ? (
                  <div className={`text-sm ${isDark ? 'text-blue-100' : 'text-gray-700'} leading-relaxed space-y-2`}>
                    {video.visual_description.split(/[.!?]+/).filter(s => s.trim().length > 0).map((sentence, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                        <span>{highlightTimestamps ? highlightTimestamps(sentence.trim()) : sentence.trim()}{idx < video.visual_description.split(/[.!?]+/).filter(s => s.trim().length > 0).length - 1 && '.'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'} italic`}>No visual description available</p>
                )}
              </div>
            </div>

            {/* Column 4: Most Distinct Thing / Text Overlay - 1st metadata column (furthest from video) */}
            <div className={`${isDark ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg border flex flex-col w-full aspect-[9/16]`}>
              <div className="flex items-center gap-2 mb-3 p-4 pb-2 flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Most Distinct / Text Overlay</h3>
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {/* Most Distinct Thing */}
                {video.most_distinct_thing ? (
                  <div className="mb-4">
                    <div className={`text-xs font-semibold ${isDark ? 'text-green-300' : 'text-green-700'} uppercase mb-2`}>Most Distinct Thing</div>
                    <div className={`text-sm ${isDark ? 'text-green-100' : 'text-gray-700'} leading-relaxed`}>
                      {highlightTimestamps ? highlightTimestamps(video.most_distinct_thing) : video.most_distinct_thing}
                    </div>
                  </div>
                ) : null}
                
                {/* Text Overlay */}
                {video.text_overlay_description ? (
                  <div className={video.most_distinct_thing ? 'mt-4 pt-4 border-t border-green-200' : ''}>
                    <div className={`text-xs font-semibold ${isDark ? 'text-green-300' : 'text-green-700'} uppercase mb-2`}>Text Overlay</div>
                    <div className={`text-sm ${isDark ? 'text-green-100' : 'text-gray-700'} leading-relaxed space-y-2`}>
                      {video.text_overlay_description.split(/[.!?]+/).filter(s => s.trim().length > 0).map((sentence, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                          <span>{highlightTimestamps ? highlightTimestamps(sentence.trim()) : sentence.trim()}{idx < video.text_overlay_description.split(/[.!?]+/).filter(s => s.trim().length > 0).length - 1 && '.'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  !video.most_distinct_thing && (
                    <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'} italic`}>No text overlay description available</p>
                  )
                )}
                
                {/* Original Caption */}
                {video.apify_caption && (
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <div className={`text-xs font-semibold ${isDark ? 'text-green-300' : 'text-gray-600'} uppercase mb-1`}>Original Caption</div>
                    <div className={`text-sm ${isDark ? 'text-green-100' : 'text-gray-700'} leading-relaxed`}>
                      {video.apify_caption}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Metadata - Trend, Tags, Hashtags */}
          {video && (
            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'} space-y-3`}>
              {/* Trend */}
              {video.trends && video.trends.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-gray-500'} uppercase mb-2`}>Trend</div>
                  <div className="flex flex-wrap gap-2">
                    {video.trends.map((trend, idx) => (
                      <span key={idx} className={`text-xs ${isDark ? 'bg-purple-900/50 text-purple-200' : 'bg-purple-100 text-purple-700'} px-2 py-1 rounded`}>
                        {typeof trend === 'object' ? trend.trend_name : trend}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tags */}
              {video.user_tags && video.user_tags.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-gray-500'} uppercase mb-2`}>Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {video.user_tags.map((tag, idx) => (
                      <span key={idx} className={`text-xs ${isDark ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700'} px-2 py-1 rounded`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Hashtags */}
              {video.apify_hashtags && video.apify_hashtags.length > 0 && (
                <div>
                  <div className={`text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-gray-500'} uppercase mb-2`}>Hashtags</div>
                  <div className="flex flex-wrap gap-2">
                    {video.apify_hashtags.slice(0, 10).map((hashtag, idx) => (
                      <span key={idx} className={`text-xs ${isDark ? 'bg-zinc-700 text-zinc-200' : 'bg-gray-100 text-gray-700'} px-2 py-1 rounded`}>
                        #{hashtag.replace(/^#/, '')}
                      </span>
                    ))}
                    {video.apify_hashtags.length > 10 && (
                      <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>+{video.apify_hashtags.length - 10} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPreviewModal;
